// Legacy KP11's scheduling policy intentionally differs from Core OOE's
// generic "any new center" policy. In particular, Marketing Center alone does
// not schedule a call. Keep this isolated so non-partner roles retain the
// shared Core OOE onboarding rules.
import {MC_2_0_SIGNATUREPLANS} from '../../../../utils/constants'
import {itemRequiresScheduleCall} from '../../../../utils/scheduling-call-rules'

const TARGET_CODES = new Set([
  'seobase-5-thryv-m2m',
  'tl-mc-5-thryv-m2m',
  'addon-socialadsbronze-5-m2m',
  'addon-socialadsgold-5-m2m',
  'addon-socialadssilver-5-m2m',
  'addon-6-mc-socialcontentgen-m2m',
  'wc_plus-5-thryv-m2m',
])

const SEO_OR_TL_CODES = new Set(['seobase-5-thryv-m2m', 'tl-mc-5-thryv-m2m'])
const DESIGN_CODES = new Set([
  'thryvdesg2',
  'addon-5-website_difm',
  'thryv5page',
])
const FALLBACK_MC_CODES = new Set([
  'mc_prov2-5-thryv-m2m',
  'signature-5-thryv-annual',
])
const SIGNATURE_PLUS_CODES = new Set([
  'signature_plus-5-thryv-m2m',
  'signature_plus-5-thryv-annual',
])

const itemCode = item => item?.productCode || item?.code
const isAlreadyIncluded = item =>
  item?.alreadyIncluded === true || item?.isAlreadyAdded === true
const isNewOrUpgraded = item => !isAlreadyIncluded(item) || item?.isUpgraded

const flattenItems = (items = []) =>
  (items ?? []).flatMap(item => [item, ...(item?.addons ?? [])])

const includesOnboarding = item => {
  const config = item?.configuration ?? item?.configuredOptions ?? {}
  const configuredValue =
    config.includeOnboarding ?? item?.customFields?.includeOnboarding?.value
  const value = configuredValue?.value ?? configuredValue
  return String(value).toLowerCase() === 'yes'
}

/** Mirrors the legacy KP11 cartChecksForAppointmentsAndNewItem gate. */
export function keapPartnerItemsRequireScheduleCall(
  items = [],
  {roles = []} = {},
) {
  // InfluencerOps never books onboarding in ooe-app (legacy KP11 only asked
  // KeapPartner). The client books it from the payment link in ooecp-app, which
  // decides per cart which products need a call.
  if (isInfluencerOpsSchedulingRole(roles)) return false
  if (
    isPartnerOpsSchedulingRole(roles) &&
    partnerOpsMcOrKeapRequiresScheduleCall(items)
  ) {
    return true
  }
  return keapPartnerBaseRequiresScheduleCall(items)
}

// PartnerOps only: a newly added MC or Keap plan books a kickoff call on its
// own, unlike KeapPartner (where MC alone does not). Deferring to the shared
// itemRequiresScheduleCall keeps this in step with getRequiredCallTypes — it
// honors exempt centers (e.g. MC Foundation) and a declined MC onboarding — so
// a location passing this gate always has a kickoff panel to render.
const MC_CODE_PREFIX = 'mc_'
const KEAP_CODE_PREFIX = 'keap_'

const isMcOrKeapPlan = item => {
  const code = itemCode(item) ?? ''
  return (
    code.startsWith(MC_CODE_PREFIX) ||
    MC_2_0_SIGNATUREPLANS.includes(code) ||
    code.startsWith(KEAP_CODE_PREFIX)
  )
}

function partnerOpsMcOrKeapRequiresScheduleCall(items = []) {
  return (items ?? []).some(
    item =>
      !isAlreadyIncluded(item) &&
      isMcOrKeapPlan(item) &&
      itemRequiresScheduleCall(item),
  )
}

function keapPartnerBaseRequiresScheduleCall(items = []) {
  const cartItems = flattenItems(items)
  const hasTargetAlreadyIncluded = cartItems.some(
    item => TARGET_CODES.has(itemCode(item)) && isAlreadyIncluded(item),
  )
  const hasTargetNewOrUpgraded = cartItems.some(
    item => TARGET_CODES.has(itemCode(item)) && isNewOrUpgraded(item),
  )
  const hasSeoOrTlNewOrUpgraded = cartItems.some(
    item => SEO_OR_TL_CODES.has(itemCode(item)) && isNewOrUpgraded(item),
  )
  const socialAds = cartItems.filter(item =>
    itemCode(item)?.includes('socialads'),
  )
  const hasNewSocialAds =
    socialAds.length > 0 && socialAds.every(isNewOrUpgraded)
  const hasNewSocialPosts = cartItems.some(
    item =>
      itemCode(item) === 'addon-6-mc-socialcontentgen-m2m' &&
      isNewOrUpgraded(item),
  )
  const hasDesignAlreadyIncluded = cartItems.some(
    item => DESIGN_CODES.has(itemCode(item)) && isAlreadyIncluded(item),
  )
  const hasNewDesign = cartItems.some(
    item => DESIGN_CODES.has(itemCode(item)) && !isAlreadyIncluded(item),
  )
  const hasNewSignaturePlus = cartItems.some(
    item => SIGNATURE_PLUS_CODES.has(itemCode(item)) && isNewOrUpgraded(item),
  )
  const hasFallbackOnboarding = cartItems.some(
    item =>
      FALLBACK_MC_CODES.has(itemCode(item)) &&
      !isAlreadyIncluded(item) &&
      includesOnboarding(item),
  )

  return (
    (hasTargetNewOrUpgraded && !hasTargetAlreadyIncluded) ||
    hasSeoOrTlNewOrUpgraded ||
    hasNewSocialAds ||
    hasNewSocialPosts ||
    (hasNewDesign && !hasDesignAlreadyIncluded) ||
    hasNewSignaturePlus ||
    hasFallbackOnboarding
  )
}

export const isInfluencerOpsSchedulingRole = roles =>
  (Array.isArray(roles) ? roles : []).includes('InfluencerOps')

export const isPartnerOpsSchedulingRole = roles =>
  (Array.isArray(roles) ? roles : []).includes('PartnerOps')

export const isKeapPartnerSchedulingRole = roles =>
  (Array.isArray(roles) ? roles : []).some(role =>
    ['KeapPartner', 'InfluencerOps', 'PartnerOps'].includes(role),
  )
